// VoyageurInsert.tsx
import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Backdrop,
  Box,
  Button,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Fade,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import HomeIcon from '@mui/icons-material/Home';
import PhoneIcon from '@mui/icons-material/Phone';
import WorkIcon from '@mui/icons-material/Work';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BadgeIcon from '@mui/icons-material/Badge';
import { useNavigate, useSearchParams } from 'react-router-dom';
import creationVoyageur from '../creationVoyageurService';
import { useSnackbar } from './SnackbarProvider';
import { useDurees, useOnboarding } from '../onboarding';
import DocumentPicker from './commun/DocumentPicker';
import ChoixCartes from './commun/ChoixCartes';
import { SectionTitle, StyledCard, StyledTextField } from './commun/formStyles';
import { chiffresSeuls, libellesChampsManquants, validerVoyageur } from '../validation/voyageur';

/** Brouillon de saisie. Les documents n'y sont JAMAIS écrits. */
const CLE_BROUILLON = 'vm_voyageur_brouillon';

const ETATS_CIVILS = [
  { valeur: 'célibataire', libelle: 'Célibataire' },
  { valeur: 'marié', libelle: 'Marié(e)' },
  { valeur: 'divorcé', libelle: 'Divorcé(e)' },
  { valeur: 'veuf', libelle: 'Veuf/Veuve' },
];

const ADRESSE_OPTIONS = [
  { valeur: 'oui', libelle: 'Oui, la même' },
  { valeur: 'non', libelle: 'Non, une autre' },
];

const VoyageurInsert: React.FC = () => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { declencherCelebration } = useOnboarding();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get('returnUrl');
  const estOnboarding = searchParams.get('onboarding') === '1';

  const [nom, setNom] = useState<string>('');
  const [prenom, setPrenom] = useState<string>('');
  const [selectedPhotoUri, setSelectedPhotoUri] = useState<string | null>(null);
  const [selectedPasseportUri, setSelectedPasseportUri] = useState<string | null>(null);
  const [adresseIdentique, setAdresseIdentique] = useState<string>('');
  const [adresse, setAdresse] = useState<string>('');
  const [codePostal, setCodePostal] = useState<string>('');
  const [ville, setVille] = useState<string>('');
  const [telephone, setTelephone] = useState<string>('');
  const [profession, setProfession] = useState<string>('');
  const [etatCivil, setEtatCivil] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Documents non persistés : on conserve l'objet File pour éviter le
  // fetch(blob:) de reconversion, qui meurt sans signal sur iOS.
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [passeportFile, setPasseportFile] = useState<File | null>(null);

  // Les erreurs ne s'affichent qu'après une première tentative d'envoi : un
  // formulaire vierge tout en rouge décourage plus qu'il n'aide.
  const [aTenteEnvoi, setATenteEnvoi] = useState<boolean>(false);
  const [brouillonRestaure, setBrouillonRestaure] = useState<boolean>(false);
  const [telephoneCompte, setTelephoneCompte] = useState<string | null>(null);

  const { reduce } = useDurees();

  const validation = validerVoyageur({
    nom,
    prenom,
    telephone,
    profession,
    etatCivil,
    adresseIdentique,
    adresse,
    codePostal,
    ville,
    photoUri: selectedPhotoUri,
    passeportUri: selectedPasseportUri,
  });
  // `erreurs` pilote l'affichage, `validation` pilote la décision.
  const erreurs = aTenteEnvoi ? validation.erreurs : {};
  const champsManquants = libellesChampsManquants(validation.erreurs);

  // Restauration du brouillon. Les documents ne peuvent pas l'être : un objet
  // File ne survit ni au rechargement ni à un changement d'onglet.
  useEffect(() => {
    try {
      const brut = sessionStorage.getItem(CLE_BROUILLON);
      if (!brut) return;
      const b = JSON.parse(brut);
      if (!b || typeof b !== 'object') return;
      setNom(b.nom || '');
      setPrenom(b.prenom || '');
      setTelephone(b.telephone || '');
      setProfession(b.profession || '');
      setEtatCivil(b.etatCivil || '');
      setAdresseIdentique(b.adresseIdentique || '');
      setAdresse(b.adresse || '');
      setCodePostal(b.codePostal || '');
      setVille(b.ville || '');
      // On n'annonce une restauration que si elle a réellement rapporté
      // quelque chose : sinon le bandeau s'affiche sur un formulaire vierge et
      // laisse croire que des documents avaient déjà été fournis.
      setBrouillonRestaure(
        Object.values(b).some((v) => typeof v === 'string' && v.trim() !== '')
      );
    } catch (e) {
      // Brouillon illisible : on repart d'un formulaire vierge, sans le signaler.
    }
  }, []);

  // Le téléphone du compte n'est proposé que hors compte société : pour une
  // entreprise, ce numéro est celui du service, pas celui du voyageur.
  useEffect(() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || 'null');
      if (u && !u.id_societe && u.telephone) setTelephoneCompte(String(u.telephone));
    } catch (e) {
      // Sans compte lisible, le raccourci n'est simplement pas proposé.
    }
  }, []);

  // Sauvegarde différée : écrire à chaque frappe ferait un JSON.stringify par caractère.
  const minuterieBrouillon = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (minuterieBrouillon.current) clearTimeout(minuterieBrouillon.current);
    minuterieBrouillon.current = setTimeout(() => {
      const champs = {
        nom,
        prenom,
        telephone,
        profession,
        etatCivil,
        adresseIdentique,
        adresse,
        codePostal,
        ville,
      };
      // Rien de saisi : on n'écrit pas de brouillon vide. Sans cette garde, le
      // simple fait d'ouvrir l'écran plus de 400 ms créait une entrée, et la
      // visite suivante affichait « Nous avons retrouvé votre saisie » à tort.
      const quelqueChoseDeSaisi = Object.values(champs).some((v) => v.trim() !== '');
      try {
        if (quelqueChoseDeSaisi) {
          sessionStorage.setItem(CLE_BROUILLON, JSON.stringify(champs));
        } else {
          sessionStorage.removeItem(CLE_BROUILLON);
        }
      } catch (e) {
        // Quota dépassé ou stockage désactivé : la saisie reste utilisable.
      }
    }, 400);
    return () => {
      if (minuterieBrouillon.current) clearTimeout(minuterieBrouillon.current);
    };
  }, [nom, prenom, telephone, profession, etatCivil, adresseIdentique, adresse, codePostal, ville]);

  const handleSubmit = async () => {
    if (isLoading) return;
    setATenteEnvoi(true);

    // Le bouton n'est plus désactivé : il DIT ce qui manque, au lieu d'être un
    // mur silencieux que l'utilisateur ne sait pas franchir.
    if (!validation.valide) {
      showSnackbar(`Il manque encore : ${champsManquants.join(', ')}.`, 'warning');
      return;
    }

    setIsLoading(true);
    try {
      const userJson = localStorage.getItem('user');
      // Sans cette sortie, un `user` disparu laissait le Backdrop plein écran ouvert
      // indéfiniment, sans message et sans issue. ProtectedRoute écarte bien le JSON
      // corrompu et l'objet vide, mais il ne contrôle qu'au montage : une déconnexion
      // depuis un autre onglet pendant la saisie passe entre les mailles.
      if (!userJson) {
        setIsLoading(false);
        showSnackbar('Votre session a expiré. Reconnectez-vous pour continuer.', 'error');
        navigate('/login', { replace: true });
        return;
      }

      const user = JSON.parse(userJson);
      await creationVoyageur({
        nom,
        prenom,
        idUtilisateur: String(user.id_utilisateur),
        photoUri: selectedPhotoUri,
        passeportUri: selectedPasseportUri,
        adresse,
        codePostal,
        ville,
        // Le backend attend des chiffres seuls. Le filtre s'appliquait à la
        // frappe, empêchant de saisir « +33 » ou des espaces et donnant
        // l'impression d'un champ cassé ; il n'agit plus qu'à l'envoi.
        telephone: chiffresSeuls(telephone),
        profession,
        etatCivil,
        photoFile,
        passeportFile,
      });
      try {
        sessionStorage.removeItem(CLE_BROUILLON);
      } catch (e) {
        // Sans conséquence : le brouillon expire avec l'onglet.
      }
      setIsLoading(false);
      // Rediriger vers le formulaire d'origine si returnUrl est présent
      // Le formulaire du pays n'a AUCUN garde-fou : sans formState il s'affiche à
      // 0 € et laisse partir une demande sans titre ni prix. On refuse donc d'y
      // retourner quand le contexte du visa a été perdu (onglet fermé, sessionStorage
      // vidé) — un seul contrôle ici protège les 56 formulaires.
      const contexteVisaPresent = (() => {
        try {
          return !!sessionStorage.getItem('formState');
        } catch (e) {
          return false;
        }
      })();

      // La célébration est portée par le provider et montée dans App.tsx : elle
      // s'affiche donc AUSSI par-dessus le formulaire du pays, ce qui est le
      // chemin nominal. Déclenchée avant la navigation, sur tous les succès.
      if (estOnboarding) declencherCelebration('voyageur');

      if (returnUrl && contexteVisaPresent) {
        // Le visa était déjà choisi : on y retourne, contexte intact.
        navigate(`${returnUrl}?fromCreation=true`);
      } else if (returnUrl) {
        showSnackbar(
          'Profil enregistré. Choisissez à nouveau votre visa : le contexte de votre demande a été perdu.',
          'warning'
        );
        navigate('/retour', { state: { tabValue: 0 } });
      } else if (estOnboarding) {
        // Venu de la feuille de route sans visa choisi : Destinations est l'étape suivante.
        navigate('/retour', { state: { tabValue: 0 } });
      } else {
        // Onglet 1 = Voyageurs. L'onglet 2 (Demandes) était une erreur : la carte
        // du voyageur qui vient d'être créé se trouve dans l'onglet Voyageurs.
        navigate('/retour', { state: { tabValue: 1 } });
      }
    } catch (error) {
      console.error("Erreur lors de l'envoi:", error);
      setIsLoading(false);
      // creationVoyageur relève désormais au lieu d'avaler l'erreur : cette
      // branche est atteignable, et doit le dire à l'utilisateur.
      showSnackbar(
        "La création du profil voyageur a échoué. Vérifiez votre connexion, puis réessayez.",
        'error'
      );
    }
  };

  return (
    <Box sx={{ backgroundImage: 'url(https://beta.visamonde.com/assets/mobile/fond.jpg)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', minHeight: '100vh', py: { xs: 2, sm: 3 } }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: { xs: 3, sm: 4 },
            borderRadius: 3,
            background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
            color: 'white',
          }}
        >
          <Box display="flex" alignItems="center">
            <IconButton
              onClick={() => navigate(-1)}
              sx={{
                color: 'white',
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.3)' },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box ml={2}>
              <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.4rem', sm: '2rem' } }}>
                Qui voyage ?
              </Typography>
              <Typography variant="subtitle1" sx={{ opacity: 0.9, mt: 0.5, fontSize: { xs: '0.85rem', sm: '1rem' } }}>
                Écrivez les noms exactement comme sur le passeport, accents compris
              </Typography>
            </Box>
          </Box>
        </Paper>

        {brouillonRestaure && (
          <Alert severity="success" sx={{ mb: 3, borderRadius: 3 }}>
            <AlertTitle>Nous avons retrouvé votre saisie</AlertTitle>
            Les informations que vous aviez commencé à remplir ont été restaurées. La photo
            d'identité et le scan du passeport sont à sélectionner à nouveau : pour votre
            sécurité, les documents ne sont jamais conservés.
          </Alert>
        )}

        {estOnboarding && (
          <Alert severity="info" sx={{ mb: 3, borderRadius: 3 }}>
            Étape 3 sur 4 — le profil du voyageur. Munissez-vous du passeport : le scan de
            sa page photo est obligatoire, ainsi qu'une photo d'identité.
          </Alert>
        )}

        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={isLoading}
        >
          <CircularProgress color="inherit" size={60} />
        </Backdrop>

        <Grid container spacing={3}>
          {/* Informations personnelles */}
          <Grid item xs={12} md={6}>
            <StyledCard>
              <CardContent sx={{ p: 4 }}>
                <SectionTitle variant="h6">
                  <PersonIcon color="primary" />
                  Informations personnelles
                </SectionTitle>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <StyledTextField
                      fullWidth
                      label="Nom"
                      value={nom}
                      onChange={(e) => setNom(e.target.value)}
                      variant="outlined"
                      error={!!erreurs.nom}
                      helperText={erreurs.nom}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <StyledTextField
                      fullWidth
                      label="Prénom"
                      value={prenom}
                      onChange={(e) => setPrenom(e.target.value)}
                      variant="outlined"
                      error={!!erreurs.prenom}
                      helperText={erreurs.prenom}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1, fontSize: '0.9rem', color: '#555' }}
                    >
                      <FavoriteIcon fontSize="small" /> État civil
                    </Typography>
                    <ChoixCartes
                      options={ETATS_CIVILS}
                      valeur={etatCivil}
                      onChange={setEtatCivil}
                      ariaLabel="État civil du voyageur"
                      erreur={erreurs.etatCivil}
                      reduce={reduce}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </StyledCard>

            {/* Contact */}
            <StyledCard>
              <CardContent sx={{ p: 4 }}>
                <SectionTitle variant="h6">
                  <PhoneIcon color="primary" />
                  Contact et profession
                </SectionTitle>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <StyledTextField
                      fullWidth
                      label="Téléphone"
                      value={telephone}
                      onChange={(e) => setTelephone(e.target.value)}
                      variant="outlined"
                      type="tel"
                      error={!!erreurs.telephone}
                      helperText={erreurs.telephone ?? 'Exemple : 06 12 34 56 78'}
                      inputProps={{ inputMode: 'tel' }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                    {telephoneCompte && telephone.trim() === '' && (
                      <Chip
                        label={`Utiliser mon numéro (${telephoneCompte})`}
                        size="small"
                        variant="outlined"
                        onClick={() => setTelephone(telephoneCompte)}
                        sx={{ mt: 1, borderColor: '#2e7d32', color: '#2e7d32' }}
                      />
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <StyledTextField
                      fullWidth
                      label="Profession"
                      value={profession}
                      onChange={(e) => setProfession(e.target.value)}
                      variant="outlined"
                      error={!!erreurs.profession}
                      helperText={erreurs.profession}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <WorkIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </StyledCard>
          </Grid>

          {/* Documents et adresse */}
          <Grid item xs={12} md={6}>
            <StyledCard>
              <CardContent sx={{ p: 4 }}>
                <SectionTitle variant="h6">
                  <PhotoCameraIcon color="primary" />
                  Documents d'identité
                </SectionTitle>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <DocumentPicker
                      documentType="Photo d'identité"
                      onDocumentSelected={(uri, fichier) => {
                        setSelectedPhotoUri(uri);
                        setPhotoFile(fichier);
                      }}
                      modeOptions={['photo', 'selectPhoto']}
                      icon={<PhotoCameraIcon />}
                      erreur={erreurs.photoUri}
                    />
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'info.lighter', borderRadius: 2, border: '1px solid', borderColor: 'info.main' }}>
                      <Typography variant="caption" color="info.main" sx={{ display: 'block', mb: 1, fontWeight: 500 }}>
                        Exemple de photo d'identité :
                      </Typography>
                      <Box
                        component="img"
                        src="/photoexemple.jpg"
                        alt="Exemple de photo d'identité"
                        sx={{
                          width: '100%',
                          maxWidth: 200,
                          height: 'auto',
                          borderRadius: 1,
                          boxShadow: 2,
                          display: 'block',
                          mx: 'auto'
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                        Photo récente sur fond clair, visage visible
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <DocumentPicker
                      documentType="Passeport"
                      onDocumentSelected={(uri, fichier) => {
                        setSelectedPasseportUri(uri);
                        setPasseportFile(fichier);
                      }}
                      modeOptions={['photo', 'selectPhoto', 'selectPdf']}
                      icon={<BadgeIcon />}
                      erreur={erreurs.passeportUri}
                    />
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'info.lighter', borderRadius: 2, border: '1px solid', borderColor: 'info.main' }}>
                      <Typography variant="caption" color="info.main" sx={{ display: 'block', mb: 1, fontWeight: 500 }}>
                        Exemple de passeport :
                      </Typography>
                      <Box
                        component="img"
                        src="/paxexemple.jpg"
                        alt="Exemple de passeport"
                        sx={{
                          width: '100%',
                          maxWidth: 300,
                          height: 'auto',
                          borderRadius: 1,
                          boxShadow: 2,
                          display: 'block',
                          mx: 'auto'
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                        Page d'identité du passeport avec photo et informations lisibles
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </StyledCard>

            {/* Adresse */}
            <StyledCard>
              <CardContent sx={{ p: 4 }}>
                <SectionTitle variant="h6">
                  <HomeIcon color="primary" />
                  Adresse
                </SectionTitle>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography sx={{ mb: 1, fontSize: '0.9rem', color: '#555' }}>
                      L'adresse du voyageur est-elle celle qui figure sur le passeport ?
                    </Typography>
                    <ChoixCartes
                      options={ADRESSE_OPTIONS}
                      valeur={adresseIdentique}
                      onChange={setAdresseIdentique}
                      ariaLabel="Adresse du voyageur"
                      erreur={erreurs.adresseIdentique}
                      reduce={reduce}
                    />
                  </Grid>

                  {adresseIdentique === 'non' && (
                    <Fade in={true}>
                      <Grid container spacing={2} sx={{ mt: 0 }}>
                        <Grid item xs={12}>
                          <StyledTextField
                            fullWidth
                            label="Adresse"
                            value={adresse}
                            onChange={(e) => setAdresse(e.target.value)}
                            variant="outlined"
                            error={!!erreurs.adresse}
                            helperText={erreurs.adresse}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <HomeIcon fontSize="small" />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <StyledTextField
                            fullWidth
                            label="Code Postal"
                            value={codePostal}
                            onChange={(e) => setCodePostal(e.target.value)}
                            variant="outlined"
                            type="tel"
                            error={!!erreurs.codePostal}
                            helperText={erreurs.codePostal}
                            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <StyledTextField
                            fullWidth
                            label="Ville"
                            value={ville}
                            onChange={(e) => setVille(e.target.value)}
                            variant="outlined"
                            error={!!erreurs.ville}
                            helperText={erreurs.ville}
                          />
                        </Grid>
                      </Grid>
                    </Fade>
                  )}
                </Grid>
              </CardContent>
            </StyledCard>
          </Grid>

          {/* Bouton de soumission */}
          <Grid item xs={12}>
            {aTenteEnvoi && champsManquants.length > 0 && (
              <Alert
                severity="warning"
                aria-live="polite"
                sx={{ mb: 2, borderRadius: 3, maxWidth: 640, mx: 'auto' }}
              >
                <AlertTitle>Il manque encore {champsManquants.length} élément{champsManquants.length > 1 ? 's' : ''}</AlertTitle>
                {champsManquants.join(', ')}.
              </Alert>
            )}

            <Box sx={{ mt: 2, mb: 3, display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleSubmit}
                startIcon={<CheckCircleIcon />}
                sx={{
                  borderRadius: 3,
                  py: 1.5,
                  px: { xs: 4, sm: 6 },
                  width: { xs: '100%', sm: 'auto' },
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  textTransform: 'none',
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
                  boxShadow: '0 4px 14px rgba(46,125,50,0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1b5e20 0%, #145218 100%)',
                    boxShadow: '0 6px 20px rgba(46,125,50,0.4)',
                  },
                }}
              >
                Enregistrer le profil voyageur
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default VoyageurInsert;